import "./classwork.css";
import { Dispatch, useContext, useEffect, useState } from "react";
import { FAClose } from "../../components/atoms/fontawesomeIcon/Fontawesome";
import { useLocation, useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import { AppContext } from "../../context/context";
import { ICurrentUser, ISessionsContent } from "../../interface/interface";
import { decryptData, getTimeSpent, isTokenValid } from "../../utils/utils";
import { userAction } from "../../reduxStore";
import { ls } from "../../localStorage";
import apis from "../../apis/api";
import { MathJax, MathJaxContext } from "better-react-mathjax";
import QuestionProgress from "./components/QuestionProgress";
import ExplanationModal from "./components/ExplanationModal";
import { StatusCodes } from "http-status-codes";
import McqOptions from "./components/McqOptions";
import InputBox from "./components/InputBox";
import AppLoader from "../../components/atoms/animation/Animation";

declare global {
  interface Window {
    MathJax: any;
  }
}

function ClassworkQuestions() {
  const config = {
    tex2jax: {
      inlineMath: [["$", "$"]],
      displayMath: [["$$", "$$"]],
    },
  };
  const navigate = useNavigate();
  const dispatch: Dispatch<any> = useDispatch();
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const QUERY: { currentUser: ICurrentUser } | any = queryParams.get("query");
  const state: any = useContext(AppContext);
  const [currentUser, setCurrentUser]: ICurrentUser | any = useState({});
  const [currentSection, setCurrentSection]: any = useState({});
  const [sessionId, setSessionId]: any = useState("");
  const [timeSlot, setTimeSlot]: any = useState("");
  const [currentIndex, setCurrentIndex] = useState(0);
  const [questionsData, setQuestionsData] = useState<any[]>([]);
  const [startTime, setStartTime] = useState<Date>(new Date());
  const currentQuestion: any = questionsData[currentIndex];
  const txnAttempts = currentQuestion?.txnDetails;
  const [inputValue, setInputValue] = useState("");
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [selectedOptionIndex, setSelectedOptionIndex] = useState<any>([]);
  const [feedbackMessage, setFeedbackMessage] = useState("");
  const [loading, setLoading] = useState(true);
  const [showTryAgain, setShowTryAgain] = useState(false);
  const [isCorrect, setIsCorrect] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [sessionContent, setSessionContent]: ISessionsContent | any = useState({});
  const [sessionStatus, setSessionStatus]: any = useState({});
  const [Type, setType] = useState("");

  useEffect(() => {
    verifyAndSetData();
  }, []);

  useEffect(() => {
    if (window.MathJax) {
      window.MathJax?.Hub?.Queue(["Typeset", window.MathJax.Hub]);
    }
  }, [currentQuestion]);

  const verifyAndSetData = async () => {
    try {
      let currentUser: ICurrentUser | any = {};
      // console.log(QUERY);
      let query: {
        currentUser: ICurrentUser;
        currentSection: any;
        sessionId: string;
        timeSlot: string;
        type: string;
        sessionsContent: ISessionsContent;
      } = decryptData(QUERY);

      // console.log("Decrypted query: ", query);
      setCurrentSection(query.currentSection);
      setSessionId(query.sessionId);
      currentUser = query.currentUser;
      dispatch(userAction.setCurrentUser(currentUser));
      // console.log(currentUser);
      if (
        (await ls.isValid(currentUser)) &&
        (await isTokenValid(currentUser.token))
      ) {
        ls.setToken(currentUser.token);
        ls.setDBToken(currentUser.dbToken);
        setCurrentUser(currentUser);
        setCurrentSection(query.currentSection);
        setSessionId(query.sessionId);
        setTimeSlot(query.timeSlot);
        setSessionContent(query.sessionsContent);
        setType(query.type);

        const sessionStatusRes = await apis._fetchSessionStatus(currentUser.id, query.sessionId);
        console.log("Session Status Response: ", sessionStatusRes.data);
        setSessionStatus(sessionStatusRes.data);
      } else {
        console.log("error while setting local storage");
        state.messageForParent("close");
        navigate("/pageNotFound");
      }
    } catch (error) {
      console.log(error);
      state.messageForParent("close");
      navigate("/pageNotFound");
    }
  };

  // api for fetching questions
  const fetchQuestions = async () => {
    try {
      setLoading(true);
      const queRes = await apis._questionsDetails(
        currentSection.q_ids,
        currentUser.country_code
      );

      if (queRes.status == StatusCodes.CREATED) {
        const txnRes = await apis._questionsTransactionsExist(
          sessionId,
          currentUser.id,
          timeSlot,
          currentSection?.q_ids
        );
        // Add total_attempted and isAttemptedBoth fields
        const questionsData = queRes.data.map(
          (item: { _id: string; data: { _id: string }; txnDetails: any }) => {
            const txnDetails = txnRes.data.filter(
              (txn: {
                question_id: string;
                status: boolean;
                txnDetails: any;
              }) => item._id.toString() === txn.question_id.toString()
            );

            item.txnDetails = txnDetails[0].txnDetails;
            return item;
          }
        );

        // console.log("Questions Data: ", questionsData);
        setQuestionsData(questionsData);
        setStartTime(new Date());
      }
    } catch (error) {
      console.error("Error fetching questions: ", error);
    } finally {
      setLoading(false); // 🔹 Stop loader no matter what
    }
  };

  // api for fetching question transactions
  useEffect(() => {
    if (currentSection?.q_ids && currentUser?.country_code) {
      fetchQuestions();
    }
  }, [currentSection, currentUser]);

  // console.log("Attempts...", txnAttempts);

  const initialize = (type: string) => {
    setStartTime(new Date());
    setIsSubmitted(false);
    setShowTryAgain(false);
    setInputValue("");
    setSelectedOptionIndex([]);
    setFeedbackMessage("");

    if (type === "next") {
      if (currentIndex < questionsData.length - 1)
        setCurrentIndex(currentIndex + 1);
    } else if (type === "prev") {
      if (currentIndex > 0) setCurrentIndex(currentIndex - 1);
    }
  };

  function isSubmitDisable() {
    const question_type = currentQuestion?.question_type;
    const disable = (question_type === "INPUT" && inputValue.trim() === "") ||
      (question_type !== "INPUT" && selectedOptionIndex?.length == 0) ? true : false;
    return disable;
  }

  function setFeedback(isCorrect: boolean) {
    let feedbackMessage = "";
    if (isCorrect) {
      feedbackMessage = "Excellent! You got it right!";
    } else {
      if (txnAttempts?.length === 0) {
        feedbackMessage = "Are you sure? Try again! You have one more chance!";
      } else if (txnAttempts?.length === 1) {
        feedbackMessage =
          "Sorry Buddy! You missed it again! Better luck next time.";
      }
    }
    setFeedbackMessage(feedbackMessage);
    setIsCorrect(isCorrect);
  }

  function sendPoints(isCorrect: boolean) {
    let pointsToSend = 0;
    if (isCorrect) {
      if (txnAttempts?.length === 0) {
        // First attempt correct → full points
        pointsToSend = currentQuestion?.difficulty_level + 1;
      } else if (txnAttempts?.length === 1) {
        // Second attempt correct → half points
        pointsToSend = (currentQuestion?.difficulty_level + 1) / 2;
      }
    } else {
      // Incorrect answer in all attempts → 0 points
      pointsToSend = 0;
    }
    return pointsToSend;
  }

  const getSelfEvaluatedAnswerObj = (isCorrect: boolean): { isCorrect: boolean, answerGiven: String | String[] } => {
    return { isCorrect, answerGiven: inputValue.trim().toLowerCase() };
  }

  const getSystemEvaluatedAnswerObj = (): { isCorrect: boolean, answerGiven: String | String[] } => {
    const correctAnswers = currentQuestion?.data?.answers;
    const question_type = currentQuestion?.question_type;

    const returnObj = { isCorrect: false, answerGiven: "" }
    if (question_type === "INPUT") {
      returnObj.answerGiven = inputValue.trim().toLowerCase();
      returnObj.isCorrect = correctAnswers[0]?.toString().trim().toLowerCase() === returnObj.answerGiven;
    }
    else // question_type === "MULTIPLE"  question_type === "MCQ" 
    {
      returnObj.isCorrect = selectedOptionIndex.every((idx: any) => correctAnswers.includes(idx));
      returnObj.answerGiven = selectedOptionIndex;
    }
    return returnObj;
  };

  // function for submitting answers

  const onClickSubmit = () => {
    if (isSubmitDisable()) return;
    if (!currentQuestion.isSystemEvaluated) {
      setShowModal(true)
      return;
    }

    const answerObj = getSystemEvaluatedAnswerObj();
    handleSubmit(answerObj);
  }


  const handleSubmit = async (answerObj: { isCorrect: boolean, answerGiven: String | String[] }) => {
    const { isCorrect, answerGiven } = answerObj;
    setIsSubmitted(true);
    setLoading(true);

    // ✅ Determine feedback message
    setFeedback(isCorrect);

    // ✅ Points calculation
    const pointsToSend = sendPoints(isCorrect);

    let time_spent = getTimeSpent(startTime);

    const submissionPayload = {
      user_id: currentUser.id,
      session_id: sessionId,
      question_type: currentQuestion?.question_type,
      type: currentSection?.category,
      time_taken: time_spent,
      isCorrect,
      timeSlot,
      question_id: currentQuestion._id,
      answer_given: answerGiven,
      isTimeOver: false,
      score: pointsToSend,
      lo_id: currentQuestion.learning_objective_id[0],
    };

    // console.log("Payload...",submissionPayload)

    try {
      const response: any = await apis._questionsTransactions(
        submissionPayload
      );

      const latestTxnResponse = questionsData.map((item: any, inx: number) => {
        if (item._id === currentQuestion._id) {
          item.txnDetails.push(response.data[0][0]);
        }
        return item;
      });

      setQuestionsData(latestTxnResponse);

      //chek if all questions are attempted and then fetch session status to update the section status accordingly
      const allAttempted = latestTxnResponse.every((item: any) => item.txnDetails.length > 0
        && (item.txnDetails[0]?.isCorrect === true || item.txnDetails.length === 2));

      console.log("All Attempted: ", allAttempted);
      if (allAttempted) {
        let totalScore = 0;
        let userScore = 0;
        let totalTimeTaken = 0;

        latestTxnResponse.forEach(element => {
          const isCorrect = element.txnDetails.some((txn: any) => txn.isCorrect === true);
          const score = element.difficulty_level + 1;
          const attempted = element.txnDetails.length;
          const timeTaken = element.txnDetails.reduce((acc: number, txn: any) => acc + txn.time_taken, 0);

          totalTimeTaken = totalTimeTaken + timeTaken;
          totalScore = totalScore + score;
          if (isCorrect) {
            userScore = userScore + getScoreBasedonAttempted(attempted, score, isCorrect);
          }
        });

        console.log("Total Score: ", totalScore, " User Score: ", userScore, " Total Time Taken: ", totalTimeTaken);
        console.log("Type: ", Type);

        if (Type === 'classwork') {
          const isLastSectionOfSession = checkIsThisLastSection();
          const checkIfClassworkIsLastSection = checkIsThisClassroomLastSection();

          const data = {
            category: currentSection?.category,
            status: "COMPLETED",
            max_score: totalScore,
            score: userScore,
            session_status: isLastSectionOfSession === true ? 'COMPLETED' : 'INCOMPLETED',
            time_taken: totalTimeTaken,
            classwork_status: checkIfClassworkIsLastSection === true ? 'COMPLETED' : 'INCOMPLETED'
          }
          await apis._updateClassWorkSessionStatus(sessionId, currentUser.id, timeSlot, data);
        }
        if (Type === 'homework') {
          const isLastSectionOfSession = checkIsThisLastSection();
          const checkIfHomeworkIsLastSection = checkIsThisHomeworkLastSection();
          let data = {
            category: currentSection?.category,
            status: "COMPLETED",
            max_score: totalScore,
            score: userScore,
            time_taken: totalTimeTaken,
            session_status: isLastSectionOfSession === true ? 'COMPLETED' : 'INCOMPLETED',
            homework_id: currentSection.homework_id,
            homework_status: checkIfHomeworkIsLastSection === true ? 'COMPLETED' : 'INCOMPLETED'
          }
          await apis._updateHomeWorkSessionStatus(sessionId, currentUser.id, timeSlot, data);
        }

        const sessionStatusRes = await apis._fetchSessionStatus(currentUser.id, sessionId);
        setSessionStatus(sessionStatusRes.data);
      }
    } catch (error) {
      console.error("Error submitting: ", error);
    } finally {
      setLoading(false); // 🔹 Stop loader
      setShowModal(false)
    }
  };

  // console.log("Selected options...", selectedOptionIndex);

  const isReviewMode =
    (txnAttempts?.length === 1 && txnAttempts[0]?.isCorrect) ||
    txnAttempts?.length === 2;

  useEffect(() => {
    const showtryagain = txnAttempts?.length === 1 && !txnAttempts[0]?.isCorrect;
    setShowTryAgain(showtryagain);
  }, [txnAttempts?.length, currentQuestion?.id]);


  const getScoreBasedonAttempted = (total_attempted: number, score: number, checkIsCorrect: boolean) => {
    if (currentSection.qType === 'RF' || checkIsCorrect === false) {
      return score;
    }
    let qScore = total_attempted === 1 ? score : (score) / 2;
    return qScore;
  }

  const checkIsThisLastSection = () => {
    let checkClassroomLastSection = checkIsThisClassroomLastSection();
    let checkHomeworkroomLastSection = checkIsThisHomeworkLastSection();
    if (checkClassroomLastSection === true && checkHomeworkroomLastSection === true) {
      return true;
    }
    return false;
  }

  const checkIsThisClassroomLastSection = () => {
    let classroomSectionCount = 0;
    let totalStatusSectionClassworkCount = 0;
    sessionContent.classwork.forEach((element: { category: string; }) => {
      if (element.category !== 'LN') {
        classroomSectionCount = classroomSectionCount + 1;
        if (sessionStatus.classwork && getStatusOfSection(sessionStatus.classwork, `${element.category.toLowerCase()}_status`) === 'COMPLETED') {
          totalStatusSectionClassworkCount = totalStatusSectionClassworkCount + 1;
        }
      }
    });
    let totalSectionSection = classroomSectionCount;
    let totalStatusSection = totalStatusSectionClassworkCount;
    if ((totalSectionSection - totalStatusSection) === 1 || (totalSectionSection - totalStatusSection) == 0) {
      return true;
    }
    return false;
  }

  const checkIsThisHomeworkLastSection = () => {
    let homeworkSectionCount = 0;
    let totalStatusSectionHomeworkCount = 0;
    sessionContent.homework.forEach((element: { data: any[]; _id: { toString: () => any; }; }) => {
      element.data.forEach((elementSec: { category: string; }) => {
        if (elementSec.category !== 'LN') {
          homeworkSectionCount = homeworkSectionCount + 1;
          let homeworkStatus = sessionStatus == null || sessionStatus.homework === undefined ? [] : sessionStatus.homework.filter((p: { homework_id: { toString: () => any; }; }) => p.homework_id.toString() === element._id.toString())
          if (homeworkStatus.length > 0 && getStatusOfSection(homeworkStatus[0], `${elementSec.category.toLowerCase()}_status`) === 'COMPLETED') {
            totalStatusSectionHomeworkCount = totalStatusSectionHomeworkCount + 1;
          }
        }
      });
    });
    let totalSectionSection = homeworkSectionCount;
    let totalStatusSection = totalStatusSectionHomeworkCount;
    if (totalSectionSection - totalStatusSection === 1 || totalSectionSection - totalStatusSection === 0) {
      return true;
    }
    return false;
  }

  const getStatusOfSection = (sessionObj: { [x: string]: any; hasOwnProperty: (arg0: any) => any; }, key: any) => {
    if (sessionObj.hasOwnProperty(key)) {
      return sessionObj[key];
    } else {
      return "";
    }
  }

  return (
    <div className="classwork-section">
      {loading ? (
        <AppLoader />
      ) : (
        <div className="main-container">
          {
            showModal && (
              <div className="evaluate-modal-overlay">
                <div className="evaluate-modal-content" onClick={(e) => e.stopPropagation()}>
                  <div className="evaluate-modal-body">
                    <div className="evaluate-heading">Evaluate the question on your own!</div>
                    <div className="evaluate-given-answer">
                      <h6>Your answer:</h6>
                      <p>{inputValue}</p>
                    </div>
                    <div className="evaluate-explanation">
                      <h6>Explanation:</h6>
                      <p>{currentQuestion?.data?.explanation}</p>
                    </div>
                    <div className="evaluate-confirm">
                      <h6>Do you think you have answered the question correctly?</h6>
                      <div className="evaluate-buttons">
                        <button className="evaluate-btn" onClick={() => {
                          handleSubmit(getSelfEvaluatedAnswerObj(true));
                        }}>YES</button>
                        <button className="evaluate-btn" onClick={() => {
                          handleSubmit(getSelfEvaluatedAnswerObj(false));
                        }}>NO</button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )
          }
          <div
            className="back-btn"
            onClick={() => state.messageForParent("close")}
          >
            <FAClose size="xl" color="#000" />
          </div>
          <div className="category-title-container">
            <h1>{currentSection?.name}</h1>
          </div>
          <div className="questions-info-container">
            <QuestionProgress
              currentIndex={currentIndex}
              totalQuestions={questionsData.length}
            />
            <div className="video-points-container">
              <div className="points-container">
                Points: {currentQuestion?.difficulty_level + 1}
              </div>
            </div>
          </div>
          <div className="classwork-questions-wrapper">
            <div className="question-text">
              {currentQuestion?.isLatex ? (
                <MathJaxContext config={config} version={2}>
                  <div
                    style={{ whiteSpace: "normal", overflowWrap: "anywhere" }}
                  >
                    <MathJax>{currentQuestion?.data?.question}</MathJax>
                  </div>
                </MathJaxContext>
              ) : (
                <div
                  dangerouslySetInnerHTML={{
                    __html: currentQuestion?.data?.question,
                  }}
                />
              )}
            </div>
            {currentQuestion?.data?.q_image && (
              <img src={currentQuestion.data?.q_image} alt="" />
            )}
          </div>
          <div className="review-explanation-container">
            {(showTryAgain || isReviewMode) && (
              <ExplanationModal
                txnAttempts={txnAttempts}
                currentQuestion={currentQuestion}
                hint={showTryAgain}
              />
            )}
          </div>
          <div className="input-options-container">
            {(currentQuestion?.question_type === "MCQ" ||
              currentQuestion?.question_type === "MULTIPLE") &&
              currentQuestion?.data?.options.length > 0 && (
                <McqOptions
                  questionType={currentQuestion.question_type}
                  options={currentQuestion.data.options}
                  selectedOptionIndex={selectedOptionIndex}
                  isSubmitted={isSubmitted}
                  answers={currentQuestion?.data?.answers}
                  disable={isReviewMode ? true : showTryAgain}
                  setSelectedOptionIndex={setSelectedOptionIndex}
                />
              )}

            {currentQuestion?.question_type === "INPUT" && (
              <InputBox
                value={inputValue}
                onChange={setInputValue}
                disabled={isReviewMode ? true : showTryAgain}
              />
            )}
          </div>

          {feedbackMessage && (
            <div className={`feedback ${isCorrect ? "feedback-success" : "feedback-error"}`}>
              {feedbackMessage}
            </div>
          )}
          <div className="tryagain-submit-container">
            {showTryAgain && (
              <button className="try-btn" onClick={() => { initialize('tryAgain') }}>
                Try Again
              </button>
            )}
            {!showTryAgain && !isReviewMode && (
              <button
                onClick={onClickSubmit}
                className="submit-btn"
                disabled={isSubmitDisable()}
              >
                Submit
              </button>
            )}
          </div>
          {questionsData.length > 1 && (
            <div className="next-prev-container">
              <button
                onClick={() => initialize("prev")}
                disabled={currentIndex === 0}
                className="prev-btn"
              >
                Previous
              </button>
              <button
                onClick={() => initialize("next")}
                disabled={currentIndex === questionsData.length - 1}
                className="next-btn"
              >
                Next
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default ClassworkQuestions;
